import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CourseResourceApi } from '../../../services/api/CourseResourceApi';
import { QuizApi } from '../../../services/api/QuizApi';
import { FlashcardApi } from '../../../services/api/FlashcardApi';
import { AudioContentApi } from '../../../services/api/AudioContentApi';
import './CreateResourceWizard.css';

/**
 * CreateResourceWizard — modal wizard for attaching a Quiz / Flashcard deck /
 * Audio resource to a course. Supports two creation paths:
 *
 *   1. "Link existing" — pick from the user's own quiz sets, decks, or audio
 *      content and link it as a CourseResource (payloadRef = artifact id).
 *   2. "Create new" — deep-link to the ScoutIQ AI Tools page to author the
 *      artifact, then return here to link it. Manual entry of a payload
 *      reference is also available as an advanced/escape hatch.
 *
 * Props:
 *   open: boolean
 *   endeavorId: course id
 *   editing: optional existing resource (enables edit mode)
 *   onClose(): dismiss
 *   onSaved(): called after successful create/update so the parent reloads
 */
const KIND_LABELS = {
  quiz: 'Quiz',
  flashcard_deck: 'Flashcard Deck',
  audio: 'Audio',
};

export default function CreateResourceWizard({ open, endeavorId, editing = null, onClose, onSaved }) {
  const [step, setStep] = useState(editing ? 'metadata' : 'kind');
  const [kind, setKind] = useState(editing?.kind || 'quiz');
  const [source, setSource] = useState('link'); // 'link' | 'manual'
  const [pickedRef, setPickedRef] = useState(editing?.payloadRef || '');
  const [pickedLabel, setPickedLabel] = useState('');
  const [title, setTitle] = useState(editing?.title || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [manualRef, setManualRef] = useState(editing?.payloadRef || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setStep('metadata');
      setKind(editing.kind || 'quiz');
      setTitle(editing.title || '');
      setDescription(editing.description || '');
      setPickedRef(editing.payloadRef || '');
      setManualRef(editing.payloadRef || '');
      setSource('manual');
    } else {
      setStep('kind');
      setKind('quiz');
      setSource('link');
      setTitle('');
      setDescription('');
      setPickedRef('');
      setPickedLabel('');
      setManualRef('');
    }
    setError(null);
  }, [open, editing]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && typeof onClose === 'function') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    const finalTitle = title.trim();
    if (!finalTitle) { setError('Title is required.'); return; }
    const payloadRef = (source === 'link' ? pickedRef : manualRef).trim() || null;
    setSaving(true);
    try {
      const body = {
        kind,
        title: finalTitle,
        description: description.trim() || null,
        payloadRef,
        visibility: editing?.visibility || 'course',
      };
      const resp = editing
        ? await CourseResourceApi.update(endeavorId, editing.resourceId, body)
        : await CourseResourceApi.create(endeavorId, body);
      if (!resp.ok) {
        const msg = await resp.text().catch(() => '');
        setError(msg || `Failed to save (${resp.status})`);
        return;
      }
      if (typeof onSaved === 'function') onSaved();
      if (typeof onClose === 'function') onClose();
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const handlePicked = (ref, label) => {
    setPickedRef(String(ref));
    setPickedLabel(label || '');
    if (!title) setTitle(label || '');
    setStep('metadata');
  };

  return (
    <div
      className="create-resource-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? 'Edit resource' : 'Create resource'}
      onClick={(e) => { if (e.target === e.currentTarget && typeof onClose === 'function') onClose(); }}
    >
      <div className="create-resource-modal-inner">
        <header className="create-resource-modal-header">
          <h2>{editing ? 'Edit Resource' : 'Add Resource'}</h2>
          <button type="button" className="create-resource-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="create-resource-modal-body">
          {error && <div className="create-resource-error" role="alert">{error}</div>}

          {step === 'kind' && (
            <KindStep
              kind={kind}
              setKind={setKind}
              onNext={() => setStep('source')}
            />
          )}

          {step === 'source' && (
            <SourceStep
              kind={kind}
              source={source}
              setSource={setSource}
              onBack={() => setStep('kind')}
              onNext={() => setStep(source === 'link' ? 'pick' : 'metadata')}
            />
          )}

          {step === 'pick' && (
            <PickStep
              kind={kind}
              onBack={() => setStep('source')}
              onPicked={handlePicked}
            />
          )}

          {step === 'metadata' && (
            <MetadataStep
              kind={kind}
              source={source}
              title={title}
              setTitle={setTitle}
              description={description}
              setDescription={setDescription}
              manualRef={manualRef}
              setManualRef={setManualRef}
              pickedRef={pickedRef}
              pickedLabel={pickedLabel}
              editing={editing}
              saving={saving}
              onBack={() => {
                if (editing) { onClose && onClose(); return; }
                setStep(source === 'link' ? 'pick' : 'source');
              }}
              onSave={handleSave}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Step 1 — pick kind ─────────────────────────────────────────── */
function KindStep({ kind, setKind, onNext }) {
  return (
    <div className="create-resource-step">
      <h3>What kind of resource?</h3>
      <div className="create-resource-kind-grid">
        {Object.entries(KIND_LABELS).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`create-resource-kind-card ${kind === value ? 'active' : ''}`}
            onClick={() => setKind(value)}
          >
            <strong>{label}</strong>
          </button>
        ))}
      </div>
      <div className="create-resource-step-actions">
        <button type="button" className="btn btn-primary" onClick={onNext}>Next →</button>
      </div>
    </div>
  );
}

/* ── Step 2 — source: link or create ────────────────────────────── */
function SourceStep({ kind, source, setSource, onBack, onNext }) {
  return (
    <div className="create-resource-step">
      <h3>How would you like to add this {KIND_LABELS[kind].toLowerCase()}?</h3>
      <div className="create-resource-source-options">
        <label className={`create-resource-source-option ${source === 'link' ? 'active' : ''}`}>
          <input type="radio" name="source" value="link" checked={source === 'link'} onChange={() => setSource('link')} />
          <div>
            <strong>Link existing</strong>
            <p>Pick a {KIND_LABELS[kind].toLowerCase()} you already own.</p>
          </div>
        </label>
        <label className={`create-resource-source-option ${source === 'manual' ? 'active' : ''}`}>
          <input type="radio" name="source" value="manual" checked={source === 'manual'} onChange={() => setSource('manual')} />
          <div>
            <strong>Create new</strong>
            <p>
              Open the <Link to="/ai-tools" target="_blank" rel="noreferrer">AI Tools</Link> page to author one,
              then return here and link it. You can also paste a payload reference manually.
            </p>
          </div>
        </label>
      </div>
      <div className="create-resource-step-actions">
        <button type="button" className="btn" onClick={onBack}>← Back</button>
        <button type="button" className="btn btn-primary" onClick={onNext}>Next →</button>
      </div>
    </div>
  );
}

/* ── Step 3a — pick existing artifact ───────────────────────────── */
function PickStep({ kind, onBack, onPicked }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      let list = [];
      if (kind === 'quiz') {
        const r = await QuizApi.getQuizSets();
        list = (r.ok ? await r.json() : []).map(q => ({
          id: q.quizSetId || q.id,
          label: q.title || `Quiz ${q.quizSetId || q.id}`,
          subtitle: q.description || `${q.questionCount || ''} questions`.trim(),
        }));
      } else if (kind === 'flashcard_deck') {
        const r = await FlashcardApi.getFlashcardDecks();
        list = (r.ok ? await r.json() : []).map(d => ({
          id: d.deckId || d.id,
          label: d.title || `Deck ${d.deckId || d.id}`,
          subtitle: d.description || `${d.cardCount || ''} cards`.trim(),
        }));
      } else if (kind === 'audio') {
        const r = await AudioContentApi.getUserAudioContent();
        list = (r.ok ? await r.json() : []).map(a => ({
          id: a.audioContentId || a.id,
          label: a.title || `Audio ${a.audioContentId || a.id}`,
          subtitle: a.format || a.description || '',
        }));
      }
      setItems(list);
    } catch (e) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !filter.trim() || (i.label || '').toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <div className="create-resource-step">
      <h3>Pick an existing {KIND_LABELS[kind].toLowerCase()}</h3>
      <input
        type="search"
        className="create-resource-filter"
        placeholder="Filter by title…"
        value={filter}
        onChange={e => setFilter(e.target.value)}
      />
      {err && <div className="create-resource-error" role="alert">{err}</div>}
      {loading ? (
        <p className="create-resource-empty">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="create-resource-empty">
          No {KIND_LABELS[kind].toLowerCase()}s found. Create one in
          {' '}<Link to="/ai-tools" target="_blank" rel="noreferrer">AI Tools</Link>{' '}
          and come back.
        </p>
      ) : (
        <ul className="create-resource-list">
          {filtered.map(item => (
            <li key={item.id}>
              <button type="button" className="create-resource-list-item" onClick={() => onPicked(item.id, item.label)}>
                <strong>{item.label}</strong>
                {item.subtitle && <span>{item.subtitle}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="create-resource-step-actions">
        <button type="button" className="btn" onClick={onBack}>← Back</button>
        <button type="button" className="btn" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>
    </div>
  );
}

/* ── Step 3/4 — title / description / save ──────────────────────── */
function MetadataStep({
  kind, source, title, setTitle, description, setDescription,
  manualRef, setManualRef, pickedRef, pickedLabel, editing, saving, onBack, onSave,
}) {
  return (
    <div className="create-resource-step">
      <h3>{editing ? 'Edit details' : `Finish your ${KIND_LABELS[kind].toLowerCase()}`}</h3>

      {source === 'link' && pickedRef && !editing && (
        <div className="create-resource-summary">
          Linked artifact: <strong>{pickedLabel || `#${pickedRef}`}</strong>
        </div>
      )}

      <label className="create-resource-label">
        Title
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={`${KIND_LABELS[kind]} title`}
          required
        />
      </label>

      <label className="create-resource-label">
        Description
        <textarea
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional"
        />
      </label>

      {source === 'manual' && (
        <label className="create-resource-label">
          Payload reference
          <input
            type="text"
            value={manualRef}
            onChange={e => setManualRef(e.target.value)}
            placeholder="Artifact id or URL"
          />
          <span className="create-resource-hint">
            Paste the id/URL of the underlying artifact (e.g. quiz set id from AI Tools).
          </span>
        </label>
      )}

      <div className="create-resource-step-actions">
        <button type="button" className="btn" onClick={onBack} disabled={saving}>
          {editing ? 'Cancel' : '← Back'}
        </button>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || !title.trim()}>
          {saving ? 'Saving…' : (editing ? 'Save changes' : 'Add to course')}
        </button>
      </div>
    </div>
  );
}
